import { useState } from 'react';
import { AUDIT_ACTION_GROUPS, SYSTEM_ACTOR } from '@product-portal/shared';
import { buttonVariants } from '@/components/ui/button';
import { PageTitle } from '@/components/portal/page-title';
import { Pagination } from '@/components/portal/pagination';
import { SearchField } from '@/components/portal/search-field';
import { SectionCard } from '@/components/portal/section-card';
import { SelectField } from '@/components/portal/select-field';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { auditSearch, useAuditLog } from '@/hooks/use-admin';
import { formatDayTime, shortName } from '@/lib/format';
import { AUDIT_GROUP_LABELS } from '@/lib/labels';
import { cn } from '@/lib/utils';

/** 14 · Journal d'audit. Read only: no write route exists. */
export function AuditPage() {
  const [actorId, setActorId] = useState('');
  const [group, setGroup] = useState('');
  const [period, setPeriod] = useState('7d');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const params = { actorId, group, period, from, to, q, page };
  const log = useAuditLog(params);
  const data = log.data;
  const reset = (set: (v: string) => void) => (v: string) => {
    set(v);
    setPage(1);
  };

  return (
    <>
      <PageTitle title="Journal d'audit" subtitle="Toutes les écritures d'administration, de qualification et de synchronisation Jira. Lecture seule.">
        <a href={`/api/admin/audit/export.csv?${auditSearch({ ...params, page: 1 }).toString()}`} className={cn(buttonVariants({ variant: 'secondary' }), 'text-[13px] text-foreground no-underline hover:text-foreground')}>
          Exporter en CSV
        </a>
      </PageTitle>
      <SectionCard
        toolbar={
          <div className="flex w-full flex-wrap items-center gap-2.5">
            <SelectField label="Acteur" size="sm" value={actorId} onChange={(e) => reset(setActorId)(e.target.value)}>
              <option value="">Tous</option>
              {(data?.actors ?? []).map((a) => (
                <option key={a.id} value={a.id}>
                  {shortName(a)}
                </option>
              ))}
              <option value={SYSTEM_ACTOR}>Système</option>
            </SelectField>
            <SelectField label="Action" size="sm" value={group} onChange={(e) => reset(setGroup)(e.target.value)}>
              <option value="">Toutes</option>
              {AUDIT_ACTION_GROUPS.map((g) => (
                <option key={g} value={g}>
                  {AUDIT_GROUP_LABELS[g]}
                </option>
              ))}
            </SelectField>
            <SelectField label="Période" size="sm" value={period} onChange={(e) => reset(setPeriod)(e.target.value)}>
              <option value="7d">7 derniers jours</option>
              <option value="30d">30 derniers jours</option>
              <option value="custom">Personnalisée</option>
            </SelectField>
            {period === 'custom' && (
              <span className="flex items-center gap-1.5 text-[13px] text-foreground-secondary">
                <label htmlFor="du">du</label>
                <input id="du" type="date" value={from} onChange={(e) => reset(setFrom)(e.target.value)} className="min-h-8 rounded-control border border-input bg-card px-2 font-[inherit] text-[13px]" />
                <label htmlFor="au">au</label>
                <input id="au" type="date" value={to} onChange={(e) => reset(setTo)(e.target.value)} className="min-h-8 rounded-control border border-input bg-card px-2 font-[inherit] text-[13px]" />
              </span>
            )}
            <SearchField label="Rechercher une cible" placeholder="Cible : PP-312, équipe…" value={q} onChange={reset(setQ)} className="ml-auto max-w-72" />
          </div>
        }
        footer={<Pagination page={page} pageSize={data?.pageSize ?? 8} total={data?.total ?? 0} itemLabel="entrées" onPageChange={setPage} ariaLabel="Pagination du journal" />}
      >
        <Table minWidth={900}>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Acteur</TableHead>
              <TableHead>Action</TableHead>
              <TableHead>Cible</TableHead>
              <TableHead>Avant</TableHead>
              <TableHead>Après</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(data?.items ?? []).map((e) => (
              <TableRow key={e.id}>
                <TableCell className="whitespace-nowrap text-foreground-secondary tabular-nums">{formatDayTime(e.createdAt)}</TableCell>
                <TableCell className={cn(!e.actor && 'text-muted-foreground')}>{e.actor ? shortName(e.actor) : 'Système'}</TableCell>
                <TableCell className="font-bold">{e.actionLabel}</TableCell>
                <TableCell>{e.targetLabel}</TableCell>
                <TableCell className="text-muted-foreground">{e.beforeLabel}</TableCell>
                <TableCell>{e.afterLabel}</TableCell>
              </TableRow>
            ))}
            {data && data.items.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground">
                  Aucune entrée sur cette période.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </SectionCard>
    </>
  );
}
